import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, IconButton, Chip, Grid, Container, Paper, Divider,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery, useTheme,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Visibility as VisibilityIcon,
  Send as SendIcon, ArrowBack as ArrowBackIcon, Settings as SettingsIcon,
  People as PeopleIcon, Description as DescriptionIcon, CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import axios from "axios";
import utils from "../../utils";

const FeedbackSystem = ({ op = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [forms, setForms] = useState([]);
  const [responses, setResponses] = useState([]);
  const [editingForm, setEditingForm] = useState(null);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedForms, setExpandedForms] = useState({});
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);

  useEffect(() => {
    const fetchFormsAndResponses = async () => {
      try {
        const formsResponse = await axios.get('/api/feedforms', {
          headers: utils.getAuthHeaders(),
        });
        if (formsResponse.data.success) {
          setForms(formsResponse.data.forms);
          setResponses(formsResponse.data.responses);
        } else {
          console.log(formsResponse.data.error);
        }
      } catch (error) {
        console.log(error);
      }
    };

    fetchFormsAndResponses();
  }, []);

  const toggleExpandForm = (formId) => {
    setExpandedForms(prev => ({
      ...prev,
      [formId]: !prev[formId]
    }));
  };

  const FormBuilderDialog = ({ form, open, onSave, onCancel }) => {
    const [formData, setFormData] = useState(form || {
      title: '',
      description: '',
      questions: [],
      active: true
    });

    useEffect(() => {
      if (open) {
        setFormData(form || {
          title: '',
          description: '',
          questions: [],
          active: true
        });
      }
    }, [open, form]);

    const addQuestion = () => {
      const newQuestion = {
        id: Date.now(),
        text: '',
        type: 'text',
        options: []
      };
      setFormData({
        ...formData,
        questions: [...formData.questions, newQuestion]
      });
    };

    const updateQuestion = (questionId, field, value) => {
      setFormData({
        ...formData,
        questions: formData.questions.map(q =>
          q.id === questionId ? { ...q, [field]: value } : q
        )
      });
    };

    const updateOption = (questionId, optionIndex, value) => {
      setFormData({
        ...formData,
        questions: formData.questions.map(q => {
          if (q.id === questionId) {
            const updatedOptions = [...q.options];
            updatedOptions[optionIndex] = value;
            return { ...q, options: updatedOptions };
          }
          return q;
        })
      });
    };

    const addOption = (questionId) => {
      setFormData({
        ...formData,
        questions: formData.questions.map(q => {
          if (q.id === questionId) {
            return { ...q, options: [...q.options, ''] };
          }
          return q;
        })
      });
    };

    const removeOption = (questionId, optionIndex) => {
      setFormData({
        ...formData,
        questions: formData.questions.map(q => {
          if (q.id === questionId) {
            const updatedOptions = [...q.options];
            updatedOptions.splice(optionIndex, 1);
            return { ...q, options: updatedOptions };
          }
          return q;
        })
      });
    };

    const removeQuestion = (questionId) => {
      setFormData({
        ...formData,
        questions: formData.questions.filter(q => q.id !== questionId)
      });
    };

    const handleSave = () => {
      if (formData.title && formData.questions.length > 0) {
        onSave({
          ...formData,
          id: form?.id || null,
        });
      }
    };

    const isValidForm = formData.title && formData.questions.length > 0;

    return (
      <Dialog
        open={open}
        onClose={onCancel}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh', borderRadius: 3 }
        }}
      >
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {form ? 'Modifica Form' : 'Crea Nuovo Form'}
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ p: isMobile ? 1 : 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Titolo"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Inserisci il titolo del form"
              variant="outlined"
            />

            <TextField
              fullWidth
              label="Descrizione"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descrizione del form (opzionale)"
              variant="outlined"
              multiline
              rows={2}
            />

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>Domande</Typography>
                <Button
                  onClick={addQuestion}
                  variant="contained"
                  startIcon={<AddIcon />}
                  size="small"
                  sx={{ borderRadius: 2 }}
                >
                  Aggiungi
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: '400px', overflowY: 'auto' }}>
                {formData.questions.map((question, index) => (
                  <Paper key={question.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                        Domanda {index + 1}
                      </Typography>
                      <IconButton
                        onClick={() => removeQuestion(question.id)}
                        size="small"
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <TextField
                      fullWidth
                      value={question.text}
                      onChange={(e) => updateQuestion(question.id, 'text', e.target.value)}
                      placeholder="Inserisci la domanda"
                      variant="outlined"
                      size="small"
                      sx={{ mb: 1 }}
                    />

                    <FormControl fullWidth size="small">
                      <InputLabel>Tipo risposta</InputLabel>
                      <Select
                        value={question.type}
                        label="Tipo risposta"
                        onChange={(e) => updateQuestion(question.id, 'type', e.target.value)}
                      >
                        <MenuItem value="text">Risposta aperta</MenuItem>
                        <MenuItem value="multiple">Risposta multipla</MenuItem>
                      </Select>
                    </FormControl>

                    {question.type === 'multiple' && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'medium', mb: 1 }}>
                          Opzioni
                        </Typography>
                        {question.options.map((option, optionIndex) => (
                          <Box key={optionIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <TextField
                              fullWidth
                              value={option}
                              onChange={(e) => updateOption(question.id, optionIndex, e.target.value)}
                              placeholder={`Opzione ${optionIndex + 1}`}
                              variant="outlined"
                              size="small"
                            />
                            <IconButton
                              onClick={() => removeOption(question.id, optionIndex)}
                              size="small"
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                        <Button
                          onClick={() => addOption(question.id)}
                          variant="outlined"
                          startIcon={<AddIcon />}
                          size="small"
                          sx={{ borderRadius: 2 }}
                        >
                          Aggiungi Opzione
                        </Button>
                      </Box>
                    )}
                  </Paper>
                ))}

                {formData.questions.length === 0 && (
                  <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Typography color="text.secondary">
                      Nessuna domanda aggiunta. Clicca su "Aggiungi Domanda" per iniziare.
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onCancel} variant="outlined" sx={{ borderRadius: 2 }}>
            Annulla
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="success"
            disabled={!isValidForm}
            sx={{ borderRadius: 2 }}
          >
            {form ? 'Salva' : 'Crea'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const ResponsesViewer = ({ formId }) => {
    const form = forms.find(f => f.id === formId);
    const formResponses = responses.filter(r => r.form_id === formId);

    if (!form) return <Typography>Form non trovato</Typography>;

    return (
      <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: isMobile ? 1 : 3 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            Risposte per: {form.title}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Totale risposte: {formResponses.length}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {formResponses.map((response, index) => (
              <Paper key={response.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    Risposta #{index + 1}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(Number(response.submitted_at)).toLocaleDateString('it-IT')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {form.questions.map(question => (
                    <Box key={question.id}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {question.text}
                      </Typography>
                      <Paper sx={{ bgcolor: 'grey.50', p: 1, border: '1px solid', borderColor: 'grey.200', borderRadius: 1 }}>
                        <Typography variant="body2">
                          {response.answers[question.id] || 'Nessuna risposta'}
                        </Typography>
                      </Paper>
                    </Box>
                  ))}
                </Box>
              </Paper>
            ))}

            {formResponses.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <Typography>Nessuna risposta ricevuta per questo form</Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const FeedbackForm = ({ form }) => {
    const [answers, setAnswers] = useState({});
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async () => {
      try {
        const newResponse = {
          formId: form.id,
          answers: answers
        };

        const res = await axios.post('/api/feedformrespond', newResponse, {
          headers: utils.getAuthHeaders(),
        });

        if (res.data.success) {
          setSubmitted(true);
        } else {
          console.log(res.data.error);
        }
      } catch (error) {
        console.log(error);
      }
    };

    if (submitted) {
      return (
        <Card sx={{ boxShadow: 3, textAlign: 'center', borderRadius: 3 }}>
          <CardContent sx={{ p: isMobile ? 1 : 3 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
              Grazie per il tuo feedback!
            </Typography>
            <Typography color="text.secondary">
              La tua risposta è stata registrata con successo.
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card
        sx={{ boxShadow: 3, borderRadius: 3 }}
        onClick={() => !expandedForms[form.id] && toggleExpandForm(form.id)}
      >
        <CardContent sx={{ p: isMobile ? 1 : 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 0, fontWeight: 'bold' }}>
                {form.title}
              </Typography>
              {form.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {form.description}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {form.questions.length} domande
              </Typography>
            </Box>
            <IconButton onClick={() => expandedForms[form.id] && toggleExpandForm(form.id)}>
              {expandedForms[form.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={expandedForms[form.id]}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {form.questions.map((question, index) => (
                <Box key={question.id}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {index + 1}. {question.text}
                  </Typography>
                  {question.type === 'textarea' ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={answers[question.id] || ''}
                      onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })}
                      placeholder="Inserisci la tua risposta..."
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    />
                  ) : question.type === 'multiple' ? (
                    <FormControl fullWidth>
                      <Select
                        value={answers[question.id] || ''}
                        onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })}
                        displayEmpty
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="" disabled>
                          Seleziona un'opzione
                        </MenuItem>
                        {question.options.map((option, index) => (
                          <MenuItem key={index} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      value={answers[question.id] || ''}
                      onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })}
                      placeholder="Inserisci la tua risposta..."
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    />
                  )}
                </Box>
              ))}

              <Button
                onClick={handleSubmit}
                variant="contained"
                startIcon={<SendIcon />}
                sx={{
                  py: 1.5,
                  mt: 2,
                  borderRadius: 2,
                  boxShadow: 2,
                  '&:hover': { boxShadow: 4 },
                  width: '200px', // Fixed width
                  margin: '0 auto', // Centers the button horizontally
                  display: 'flex', // Ensures the icon and text are aligned horizontally
                  justifyContent: 'center', // Centers the content horizontally within the button
                  alignItems: 'center', // Aligns items vertically in the center
                }}
              >
                Invia Feedback
              </Button>

            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  const saveForm = async (formData) => {
    try {

      const res = await axios.post('/api/feedform', { id: editingForm ? editingForm.id : null, formData }, {
        headers: utils.getAuthHeaders(),
      });

      if (res.data.success) {
        if (editingForm) {
          setForms(forms.map(f => f.id === editingForm.id ? res.data.form : f));
        } else {
          setForms([...forms, res.data.form]);
        }
        setEditingForm(null);
        setDialogOpen(false);
      } else {
        console.log(res.data.error);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const openNewFormDialog = () => {
    setEditingForm(null);
    setDialogOpen(true);
  };

  const openEditFormDialog = (form) => {
    setEditingForm(form);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingForm(null);
  };

  const openConfirmDeleteDialog = (formId) => {
    setFormToDelete(formId);
    setConfirmDeleteDialogOpen(true);
  };

  const deleteForm = async () => {
    if (formToDelete) {
      try {
        const res = await axios.post(`/api/feedformdelete`, { id: formToDelete }, {
          headers: utils.getAuthHeaders(),
        });

        if (res.data.success) {
          setForms(forms.filter(f => f.id !== formToDelete));
          setResponses(responses.filter(r => r.form_id !== formToDelete));
          setConfirmDeleteDialogOpen(false);
          setFormToDelete(null);
        } else {
          console.log(res.data.error);
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  const toggleFormStatus = async (formId) => {
    try {
      const form = forms.find(f => f.id === formId);
      const res = await axios.post(`/api/feedformstatus`, { id: formId, active: !form.active }, {
        headers: utils.getAuthHeaders(),
      });

      if (res.data.success) {
        setForms(forms.map(f => f.id === formId ? { ...f, active: !f.active } : f));
      } else {
        console.log(res.data.error);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', p: isMobile ? 1 : 2 }}>
      <Container maxWidth="xl">
        {op && (
          <Box>
            {selectedFormId ? (
              <Box>
                <Button
                  onClick={() => setSelectedFormId(null)}
                  startIcon={<ArrowBackIcon />}
                  variant="outlined"
                  sx={{ mb: 2, borderRadius: 2 }}
                >
                  Indietro
                </Button>
                <ResponsesViewer formId={selectedFormId} />
              </Box>
            ) : (
              <Box>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                  flexDirection: isMobile ? 'column' : 'row',
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: isMobile ? 2 : 0 }}>
                    Gestione Form
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                    justifyContent: isMobile ? 'center' : 'flex-start',
                    width: isMobile ? '100%' : 'auto',
                  }}>
                    <Button
                      onClick={openNewFormDialog}
                      variant="contained"
                      startIcon={<AddIcon />}
                      sx={{ borderRadius: 2 }}
                    >
                      Nuovo
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {forms.map(form => (
                    <Card key={form.id} sx={{ boxShadow: 2, borderRadius: 2 }}>
                      <CardContent sx={{ p: isMobile ? 1 : 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              {form.title}
                            </Typography>
                            {form.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {form.description}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
                              <span>{form.questions.length} domande</span>
                              <span>{responses.filter(r => r.form_id === form.id).length} risposte</span>
                              <Chip
                                label={form.active ? 'Attivo' : 'Inattivo'}
                                size="small"
                                color={form.active ? 'success' : 'error'}
                              />
                            </Box>
                          </Box>

                          <Box sx={{ display: 'flex', gap: 0, flexDirection: isMobile ? 'column' : 'row' }}>
                            <IconButton
                              onClick={() => setSelectedFormId(form.id)}
                              color="primary"
                              title="Visualizza risposte"
                            >
                              <VisibilityIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => openEditFormDialog(form)}
                              color="warning"
                              title="Modifica form"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => toggleFormStatus(form.id)}
                              color={form.active ? 'success' : 'gray'}
                              title={form.active ? 'Disattiva' : 'Attiva'}
                            >
                              <DescriptionIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => openConfirmDeleteDialog(form.id)}
                              color="error"
                              title="Elimina form"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}

                  {forms.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      <Typography>Nessun form creato. Clicca su "Nuovo Form" per iniziare.</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            <FormBuilderDialog
              form={editingForm}
              open={dialogOpen}
              onSave={saveForm}
              onCancel={closeDialog}
            />
          </Box>
        )}

        {!op && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Form Disponibili
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {forms.filter(form => form.active).map(form => (
                <FeedbackForm key={form.id} form={form} />
              ))}

              {forms.filter(form => form.active).length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <Typography>Nessun form disponibile al momento.</Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Dialog
          open={confirmDeleteDialogOpen}
          onClose={() => setConfirmDeleteDialogOpen(false)}
        >
          <DialogTitle>Conferma Eliminazione</DialogTitle>
          <DialogContent>
            <Typography>Sei sicuro di voler eliminare questo form? Questa azione non può essere annullata.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteDialogOpen(false)} color="primary">
              Annulla
            </Button>
            <Button onClick={deleteForm} color="error">
              Elimina
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default FeedbackSystem;
